import { X, Info, AlertCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useBannerAlert, type BannerVariant } from "@/contexts/BannerAlertContext";
import { cn } from "@/lib/utils";

const variantStyles: Record<BannerVariant, { container: string; icon: string; iconEl: React.ElementType }> = {
  info: {
    container: "bg-blue-50 border-blue-300 text-blue-900",
    icon: "text-blue-600",
    iconEl: Info,
  },
  error: {
    container: "bg-red-50 border-red-300 text-red-900",
    icon: "text-red-600",
    iconEl: AlertCircle,
  },
  warning: {
    container: "bg-yellow-50 border-yellow-300 text-yellow-900",
    icon: "text-yellow-600",
    iconEl: AlertTriangle,
  },
  success: {
    container: "bg-teal-50 border-teal-300 text-teal-900",
    icon: "text-teal-600",
    iconEl: CheckCircle2,
  },
};

const BannerAlertContainer = () => {
  const { banners, removeBanner } = useBannerAlert();

  if (banners.length === 0) return null;

  return (
    <div className="container-lg px-6 pt-4 flex flex-col gap-2">
      {banners.map((banner) => {
        const { container, icon, iconEl: Icon } = variantStyles[banner.variant];
        return (
          <div
            key={banner.id}
            role="alert"
            className={cn(
              "w-full flex items-start gap-3 rounded-lg border px-4 py-3 text-sm",
              container
            )}
          >
            <Icon className={cn("w-5 h-5 mt-0.5 shrink-0", icon)} />
            <div className="flex-1">
              {banner.inline ? (
                <span>
                  <span className="font-semibold">{banner.title}</span>
                  {banner.body && <span className="ml-1">{banner.body}</span>}
                </span>
              ) : (
                <>
                  <p className="font-semibold leading-snug">{banner.title}</p>
                  {banner.body && <p className="mt-0.5 leading-snug">{banner.body}</p>}
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => removeBanner(banner.id)}
              className="shrink-0 ml-2 opacity-60 hover:opacity-100 transition-opacity"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default BannerAlertContainer;
